import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sunrise, Calendar, ListChecks, Plus, Settings2,
  CheckCircle2, XCircle, Clock, RefreshCw, AlertTriangle,
  ChevronLeft, ChevronRight, Upload, Image, Trash2, Edit2,
  Bell, BellOff, Send, Download, Filter, X,
  BarChart3, ArrowUp, ArrowDown, ArrowUpDown,
  TrendingUp, Users, ClipboardX, FileWarning, Hourglass, CalendarCheck,
  PlayCircle, Ban,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import toast from 'react-hot-toast';
import { morningActivityAPI, employeesAPI, schoolConfigAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import EmailNotificationSetup       from '../components/Common/EmailNotificationSetup';
import TelegramNotificationSetup    from '../components/Common/TelegramNotificationSetup';
import NotificationThresholdCard    from '../components/Common/NotificationThresholdCard';
import StatCard from '../components/Cards/StatCard';

// ── Constants ────────────────────────────────────────────────
const ALL_TABS = [
  { key: 'dashboard',  label: 'Dashboard',   icon: BarChart3,  adminOnly: false },
  { key: 'activities', label: 'Activities',  icon: ListChecks, adminOnly: false },
  { key: 'add',        label: 'Add Activity',icon: Plus,       adminOnly: false },
  { key: 'setup',      label: 'Setup',       icon: Settings2,  adminOnly: true  },
];

const ALL_ACCESS_ROLES = ['admin','super_admin','management','coordinator','principal','vice_principal','vice principal'];
function normaliseRole(r) { return (r||'').toLowerCase().replace(/[\s-]+/g,'_'); }
function hasAllAccess(role) {
  const n = normaliseRole(role);
  return ALL_ACCESS_ROLES.some(r => normaliseRole(r) === n);
}

// 8 real DB statuses + 2 virtual display statuses
const STATUS_CONFIG = {
  // DB statuses
  scheduled:        { label: 'Scheduled',        color: 'bg-blue-100 text-blue-700',        dot: 'bg-blue-500'        },
  pending_evidence: { label: 'Pending Evidence',  color: 'bg-purple-100 text-purple-700',    dot: 'bg-purple-500'      },
  completed:        { label: 'Completed',          color: 'bg-emerald-100 text-emerald-700',  dot: 'bg-emerald-500'     },
  not_conducted:    { label: 'Not Conducted',      color: 'bg-slate-100 text-slate-600',      dot: 'bg-slate-400'       },
  missed:           { label: 'Missed',             color: 'bg-red-100 text-red-700',          dot: 'bg-red-500'         },
  evidence_missing: { label: 'Evidence Missing',   color: 'bg-red-900/10 text-red-900',       dot: 'bg-red-900'         },
  rescheduled:      { label: 'Rescheduled',        color: 'bg-amber-100 text-amber-700',      dot: 'bg-amber-500'       },
  // Virtual display-only statuses (computed from scheduled + time checks)
  due_today:        { label: 'Due Today',          color: 'bg-yellow-100 text-yellow-700',    dot: 'bg-yellow-500'      },
  awaiting_update:  { label: 'Awaiting Update',    color: 'bg-orange-100 text-orange-700',    dot: 'bg-orange-500'      },
};

// Display statuses available in the filter dropdown
const FILTER_STATUSES = [
  { key: 'scheduled',        label: 'Scheduled (Upcoming)' },
  { key: 'due_today',        label: 'Due Today' },
  { key: 'awaiting_update',  label: 'Awaiting Update' },
  { key: 'pending_evidence', label: 'Pending Evidence' },
  { key: 'completed',        label: 'Completed' },
  { key: 'not_conducted',    label: 'Not Conducted' },
  { key: 'missed',           label: 'Missed' },
  { key: 'evidence_missing', label: 'Evidence Missing' },
  { key: 'rescheduled',      label: 'Rescheduled' },
];

const NOT_CONDUCTED_REASONS = [
  'Teacher Absent',
  'Students Unavailable',
  'Special Event',
  'Holiday',
  'Weather Conditions',
  'Other',
];

const PIE_COLORS = {
  scheduled:'#3b82f6', pending_evidence:'#a855f7', completed:'#10b981',
  not_conducted:'#94a3b8', missed:'#ef4444', evidence_missing:'#7f1d1d',
  rescheduled:'#f59e0b',
};
const PIE_RADIAN = Math.PI / 180;
function renderPieLabel({ cx, cy, midAngle, outerRadius, name, percent }) {
  if (percent < 0.04) return null;
  const r  = outerRadius * 0.62;
  const x  = cx + r * Math.cos(-midAngle * PIE_RADIAN);
  const y  = cy + r * Math.sin(-midAngle * PIE_RADIAN);
  const cfg = STATUS_CONFIG[name];
  const lbl = cfg ? cfg.label : name;
  return (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fill="white" fontSize={10} fontWeight={600}>
      <tspan x={x} dy="-6">{lbl.split(' ')[0]}</tspan>
      <tspan x={x} dy="14">{(percent * 100).toFixed(0)}%</tspan>
    </text>
  );
}

const NOTIF_TYPES = [
  { key: 'day_before',        label: 'Day Before Reminder',         icon: Bell,   desc: 'Sent at 6:00 PM, one day before the activity' },
  { key: 'ten_min_before',    label: '10-Minute Reminder',          icon: Clock,  desc: 'Sent 10 minutes before the activity start time' },
  { key: 'evidence_reminder', label: 'Evidence Submission Reminder',icon: Upload, desc: 'Sent at 3:45 PM on the activity day for pending submissions' },
];
const PLACEHOLDERS = ['{Activity_Name}', '{Activity_Date}', '{Activity_Time}', '{Responsibility_Lead}', '{School_Name}'];

// ── Helpers ──────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  const dt = typeof d === 'string' ? new Date(d + 'T00:00:00') : d;
  return isNaN(dt) ? String(d) : dt.toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric' });
}
function fmtTime(t) {
  if (!t) return '—';
  const [h, m] = (t || '').split(':').map(Number);
  if (isNaN(h)) return '—';
  const suf = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${suf}`;
}
function toDateStr(d) {
  const y = d.getFullYear(), mo = String(d.getMonth()+1).padStart(2,'0'), dy = String(d.getDate()).padStart(2,'0');
  return `${y}-${mo}-${dy}`;
}
function parseLocalDate(s) { return new Date(s + 'T00:00:00'); }
function isSameDay(a, b) { return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }
function fmtMonthYear(y, m) { return new Date(y, m, 1).toLocaleDateString('en-US', { month:'long', year:'numeric' }); }
function fmtDow(d) { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday:'short' }); }

const KNOWN_STATUSES = new Set(['scheduled','pending_evidence','completed','not_conducted','missed','evidence_missing','rescheduled']);
// Legacy DB values → normalized
const LEGACY_STATUS_MAP = { incomplete: 'missed', overdue: 'missed' };

// Compute the display/virtual status for an activity
function computeDisplayStatus(act) {
  const status = LEGACY_STATUS_MAP[act.status] ?? act.status;
  // Unknown/unmapped legacy values — treat as missed if past, scheduled if future
  if (!KNOWN_STATUSES.has(status)) {
    const actDT = new Date(`${act.scheduled_date}T${act.scheduled_time || '00:00:00'}`);
    return actDT <= new Date() ? 'missed' : 'scheduled';
  }
  if (status !== 'scheduled') return status;
  const now     = new Date();
  const today   = toDateStr(now);
  const actDate = act.scheduled_date;
  // Only same-day activities get virtual statuses
  const actDT        = new Date(`${actDate}T${act.scheduled_time || '00:00:00'}`);
  if (actDT > now)   return actDate === today ? 'due_today' : 'scheduled';
  if (actDate === today) {
    const fourHrsAfter = new Date(actDT.getTime() + 4 * 60 * 60 * 1000);
    if (now < fourHrsAfter) return 'awaiting_update'; // Same day, within 4-hour action window
  }
  return 'missed'; // Time passed and no action taken
}

// Edit allowed: admins always; teachers only if >8 hours before scheduled time and not missed
function canEdit(act, seeAll) {
  if (seeAll) return true;
  const ds = computeDisplayStatus(act);
  if (ds === 'missed' || ds === 'awaiting_update') return false;
  const actDT = new Date(`${act.scheduled_date}T${act.scheduled_time || '00:00:00'}`);
  return (actDT - new Date()) > 8 * 60 * 60 * 1000;
}
// Delete allowed: admins always; teachers not allowed on missed activities
function canDelete(act, seeAll) {
  if (seeAll) return true;
  return computeDisplayStatus(act) !== 'missed';
}

// Is evidence submission still open? (before 4 PM on scheduled_date)
function canSubmitEvidence(act) {
  if (act.status !== 'pending_evidence') return false;
  const deadline = parseLocalDate(act.scheduled_date);
  deadline.setHours(16, 0, 0, 0);
  return new Date() < deadline;
}

const inp = 'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none bg-white';

function TimeDropdown({ value, onChange }) {
  function fromVal(v) {
    if (!v) return { h: '', m: '', ap: 'AM' };
    const [hh, mm] = v.split(':').map(Number);
    return { h: String(hh % 12 || 12), m: String(mm).padStart(2, '0'), ap: hh >= 12 ? 'PM' : 'AM' };
  }
  const [parts, setParts] = React.useState(() => fromVal(value));
  React.useEffect(() => { if (!value) setParts({ h: '', m: '', ap: 'AM' }); }, [value]);

  function update(key, val) {
    const next = { ...parts, [key]: val };
    setParts(next);
    if (next.h && next.m) {
      let hh = Number(next.h);
      if (next.ap === 'PM' && hh !== 12) hh += 12;
      if (next.ap === 'AM' && hh === 12) hh = 0;
      onChange(`${String(hh).padStart(2,'0')}:${next.m}`);
    }
  }
  const sel = 'bg-transparent border-none outline-none text-sm text-slate-700 cursor-pointer focus:ring-0 py-0';
  return (
    <div className="inline-flex items-center gap-1 border border-slate-200 rounded-lg px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-primary-400 w-fit">
      <select className={`${sel} w-12 text-center`} value={parts.h} onChange={e => update('h', e.target.value)}>
        <option value="">HH</option>
        {Array.from({ length: 12 }, (_,i) => i+1).map(n => <option key={n} value={n}>{String(n).padStart(2,'0')}</option>)}
      </select>
      <span className="text-slate-400 font-semibold select-none">:</span>
      <select className={`${sel} w-12 text-center`} value={parts.m} onChange={e => update('m', e.target.value)}>
        <option value="">MM</option>
        {Array.from({ length: 60 }, (_,i) => String(i).padStart(2,'0')).map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <span className="mx-1 text-slate-200 select-none">|</span>
      <select className={`${sel} w-14`} value={parts.ap} onChange={e => update('ap', e.target.value)}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

const FS = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.scheduled;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ── Analytics Pct Card ────────────────────────────────────────
function AnalyticsCard({ label, value, color, icon: Icon }) {
  const pctColor = {
    green: 'text-emerald-600 bg-emerald-50',
    red:   'text-red-600 bg-red-50',
    purple:'text-purple-600 bg-purple-50',
    orange:'text-orange-600 bg-orange-50',
  }[color] || 'text-slate-600 bg-slate-50';
  return (
    <div className="glass-card p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pctColor}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-xs text-slate-500 font-medium">{label}</p>
        <p className={`text-2xl font-bold ${pctColor.split(' ')[0]}`}>{value}%</p>
      </div>
    </div>
  );
}

// ── Multi-date Calendar Picker ────────────────────────────────
function DatePicker({ selected, onChange }) {
  const now = new Date();
  const [viewYear,  setViewYear]  = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const days = [];
  const firstDay = new Date(viewYear, viewMonth, 1);
  const lastDay  = new Date(viewYear, viewMonth + 1, 0);
  const startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(viewYear, viewMonth, d));
  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y-1); setViewMonth(11); }
    else setViewMonth(m => m-1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y+1); setViewMonth(0); }
    else setViewMonth(m => m+1);
  }
  function toggle(d) {
    const ds = toDateStr(d);
    onChange(selected.includes(ds) ? selected.filter(x => x !== ds) : [...selected, ds]);
  }
  const DOW = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 select-none">
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-slate-100"><ChevronLeft size={16} /></button>
        <span className="text-sm font-semibold text-slate-700">{fmtMonthYear(viewYear, viewMonth)}</span>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-slate-100"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DOW.map(d => <div key={d} className="text-center text-xs font-semibold text-slate-400 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds  = toDateStr(d);
          const sel = selected.includes(ds);
          const tod = isSameDay(d, new Date());
          const sun = d.getDay() === 0;
          return (
            <button key={ds} onClick={() => toggle(d)} disabled={sun}
              className={`h-8 w-full rounded-lg text-xs font-medium transition-all ${
                sel ? 'bg-primary-600 text-white shadow-sm'
                    : sun ? 'text-slate-300 cursor-default'
                    : tod ? 'ring-2 ring-primary-400 text-primary-700 hover:bg-primary-50'
                    : 'hover:bg-slate-100 text-slate-700'
              }`}>
              {d.getDate()}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-1.5">{selected.length} date{selected.length !== 1 ? 's' : ''} selected:</p>
          <div className="flex flex-wrap gap-1">
            {[...selected].sort().map(ds => (
              <span key={ds} className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 text-xs rounded-lg px-2 py-0.5">
                {fmtDate(ds)}
                <button onClick={() => onChange(selected.filter(x => x !== ds))} className="hover:text-red-500"><X size={10} /></button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Not Conducted Modal ────────────────────────────────────────
function NotConductedModal({ activity, onClose, onSaved }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    if (!reason) return toast.error('Please select a reason');
    setSaving(true);
    try {
      await morningActivityAPI.markNotConducted(activity.id, { reason });
      toast.success('Activity marked as not conducted');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update activity');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-800">Mark as Not Conducted</h3>
            <p className="text-xs text-slate-500 mt-0.5">{activity.subject} · {fmtDate(activity.scheduled_date)}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100"><X size={17} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-600 flex items-start gap-2">
            <AlertTriangle size={15} className="text-amber-500 mt-0.5 shrink-0" />
            A reason is required. This action cannot be undone.
          </div>
          <FS label="Reason *">
            <select className={inp} value={reason} onChange={e => setReason(e.target.value)}>
              <option value="">— Select reason —</option>
              {NOT_CONDUCTED_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </FS>
        </div>
        <div className="flex gap-3 p-5 border-t border-slate-100">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !reason}
            className="flex-1 py-2.5 bg-slate-700 text-white rounded-xl text-sm font-bold hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><RefreshCw size={14} className="animate-spin" /> Saving…</> : <><Ban size={14} /> Confirm</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Evidence submit drawer ────────────────────────────────────
function EvidenceDrawer({ activity, onClose, onSaved }) {
  const [note, setNote]     = useState('');
  const [files, setFiles]   = useState([]);
  const [previews, setPreviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [evidence, setEvidence] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    morningActivityAPI.getEvidence(activity.id).then(r => {
      if (r.data) { setEvidence(r.data); setNote(r.data.note || ''); }
    }).catch(() => {});
  }, [activity.id]);

  function handleFiles(e) {
    const picked = Array.from(e.target.files || []);
    setFiles(f => [...f, ...picked]);
    setPreviews(p => [...p, ...picked.map(f => URL.createObjectURL(f))]);
  }
  function removeFile(idx) {
    setFiles(f => f.filter((_,i) => i !== idx));
    setPreviews(p => p.filter((_,i) => i !== idx));
  }

  async function handleSubmit() {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('note', note);
      files.forEach(f => fd.append('photos', f));
      await morningActivityAPI.submitEvidence(activity.id, fd);
      toast.success('Evidence submitted successfully');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to submit evidence');
    } finally { setSaving(false); }
  }

  const BASE_URL = 'http://localhost:5000';
  const isPast = !canSubmitEvidence(activity);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-800">{activity.subject}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{fmtDate(activity.scheduled_date)} · {fmtTime(activity.scheduled_time)}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {isPast && activity.status !== 'completed' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              <AlertTriangle size={14} className="inline mr-1.5" />
              Submission deadline (4:00 PM) has passed.
            </div>
          )}
          {evidence && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700">
              <CheckCircle2 size={14} className="inline mr-1.5" />
              Evidence already submitted at {evidence.submitted_at ? new Date(evidence.submitted_at).toLocaleString('en-GB', { hour:'numeric', minute:'2-digit', day:'2-digit', month:'2-digit', year:'numeric' }) : ''}
            </div>
          )}
          {evidence?.photos?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Existing Photos</p>
              <div className="grid grid-cols-3 gap-2">
                {evidence.photos.map(p => (
                  <img key={p.id}
                    src={`${BASE_URL}/uploads/morning-activity/${activity.id}/${p.filename}`}
                    alt={p.original_name} className="rounded-xl object-cover w-full h-24 bg-slate-100"
                    onError={e => { e.target.style.display='none'; }} />
                ))}
              </div>
            </div>
          )}
          <FS label="Notes / Observation">
            <textarea className={`${inp} resize-none`} rows={3} value={note}
              onChange={e => setNote(e.target.value)} placeholder="Describe what happened, observations, outcomes…" />
          </FS>
          <div>
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Upload Photos</p>
            <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={handleFiles} />
            <button onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-slate-300 rounded-xl py-4 text-sm text-slate-500 hover:border-primary-400 hover:text-primary-600 transition-colors flex flex-col items-center gap-1">
              <Image size={20} />
              Click to add photos
            </button>
            {previews.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {previews.map((url, i) => (
                  <div key={i} className="relative rounded-xl overflow-hidden">
                    <img src={url} alt="" className="w-full h-24 object-cover" />
                    <button onClick={() => removeFile(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"><X size={10} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-slate-100">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving || isPast}
            className="flex-1 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><RefreshCw size={14} className="animate-spin" /> Submitting…</> : <><CheckCircle2 size={14} /> Submit Evidence</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════
export default function MorningActivity() {
  const { user } = useAuth();
  const isAdmin   = user?.role === 'admin' || user?.role === 'super_admin';
  const seeAll    = hasAllAccess(user?.role);
  const TABS      = ALL_TABS.filter(t => !t.adminOnly || isAdmin);

  const [tab, setTab]       = useState('dashboard');
  const [stats, setStats]   = useState(null);
  const [activities, setActivities] = useState([]);
  const [leads, setLeads]   = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Activities filters
  const [filterStatus,  setFilterStatus]  = useState('');
  const [filterLead,    setFilterLead]    = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [sortField,     setSortField]     = useState('date');
  const [sortDir,       setSortDir]       = useState('desc');
  const [datePreset,    setDatePreset]    = useState('month');
  const [customStart,   setCustomStart]   = useState('');
  const [customEnd,     setCustomEnd]     = useState('');

  // Evidence drawer
  const [evidenceAct, setEvidenceAct] = useState(null);
  // Not conducted modal
  const [notConductedAct, setNotConductedAct] = useState(null);

  // Edit modal
  const [editAct,    setEditAct]    = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirm
  const [deleteId, setDeleteId] = useState(null);

  // Add form
  const [addForm,     setAddForm]     = useState({ dates:[], time:'', subject:'', description:'', category:'', leadId:'' });
  const [addSaving,   setAddSaving]   = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);

  // Notification config
  const [notifConfigs, setNotifConfigs] = useState([]);
  const [notifSaving,  setNotifSaving]  = useState(false);

  const distinctSubjects = [...new Set(activities.map(a => a.subject).filter(Boolean))].sort();

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }
  const sortedActivities = [...activities].sort((a, b) => {
    let va, vb;
    if (sortField === 'activity') { va = (a.subject||'').toLowerCase(); vb = (b.subject||'').toLowerCase(); }
    else if (sortField === 'date') { va = (a.scheduled_date||'') + (a.scheduled_time||''); vb = (b.scheduled_date||'') + (b.scheduled_time||''); }
    else if (sortField === 'lead') { va = (a.lead_name||'').toLowerCase(); vb = (b.lead_name||'').toLowerCase(); }
    else if (sortField === 'status') { va = computeDisplayStatus(a); vb = computeDisplayStatus(b); }
    else return 0;
    return sortDir === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
  });

  function getDateRange() {
    const now = new Date();
    const nowStr = toDateStr(now);
    if (datePreset === 'today')  return { startDate: nowStr, endDate: nowStr };
    if (datePreset === 'week') {
      const day = now.getDay() || 7;
      const mon = new Date(now); mon.setDate(now.getDate() - day + 1);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return { startDate: toDateStr(mon), endDate: toDateStr(sun) };
    }
    if (datePreset === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { startDate: toDateStr(start), endDate: toDateStr(end) };
    }
    if (datePreset === 'custom') return { startDate: customStart, endDate: customEnd };
    return {};
  }

  // ── Loaders ─────────────────────────────────────────────────
  const loadStats = useCallback(() => {
    morningActivityAPI.getStats().then(r => setStats(r.data)).catch(() => {});
  }, []);

  const loadActivities = useCallback(() => {
    const range = getDateRange();
    const params = { ...range };
    if (filterStatus)  params.status  = filterStatus;
    if (filterLead)    params.leadId  = filterLead;
    if (filterSubject) params.subject = filterSubject;
    setLoading(true);
    morningActivityAPI.getActivities(params)
      .then(r => setActivities(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [datePreset, customStart, customEnd, filterStatus, filterLead, filterSubject]);

  const loadLeads = useCallback(() => {
    employeesAPI.getAll().then(r => setLeads(r.data || [])).catch(() => {});
  }, []);
  const loadSubjects = useCallback(() => {
    schoolConfigAPI.getSubjects().then(r => setSubjects(r.data || [])).catch(() => {});
  }, []);
  const loadNotifConfig = useCallback(() => {
    morningActivityAPI.getNotificationConfig().then(r => {
      if (r.data?.length) {
        setNotifConfigs(r.data.map(c => ({
          ...c, enabled: Boolean(c.enabled), template: c.template || '', chat_id: c.chat_id || '',
        })));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => { loadStats(); loadLeads(); loadSubjects(); loadNotifConfig(); }, []);
  useEffect(() => { if (tab === 'activities') loadActivities(); }, [tab, loadActivities]);

  // ── Mark as Conducted ─────────────────────────────────────────
  async function handleMarkConducted(act) {
    try {
      await morningActivityAPI.markConducted(act.id);
      toast.success('Activity marked as conducted. Please submit evidence before 4:00 PM.');
      loadActivities(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update activity');
    }
  }

  // ── Add Activity ─────────────────────────────────────────────
  async function handleAdd() {
    if (!addForm.dates.length)   return toast.error('Select at least one date');
    if (!addForm.subject.trim()) return toast.error('Activity name is required');
    if (!addForm.category)       return toast.error('Activity category is required');
    if (!addForm.time)           return toast.error('Time is required');
    if (!addForm.leadId)         return toast.error('Responsibility lead is required');
    setConflictInfo(null);
    setAddSaving(true);
    try {
      const r = await morningActivityAPI.createActivities({
        dates: addForm.dates, time: addForm.time,
        subject: addForm.subject.trim(), description: addForm.description,
        activityCategory: addForm.category || null,
        responsibilityLeadId: addForm.leadId || null,
      });
      toast.success(r.data.message);
      setAddForm({ dates:[], time:'', subject:'', description:'', category:'', leadId:'' });
      setTab('activities');
      loadStats();
    } catch (e) {
      if (e.response?.status === 409) setConflictInfo(e.response.data);
      else toast.error(e.response?.data?.error || 'Failed to create activity');
    } finally { setAddSaving(false); }
  }

  // ── Edit Activity ────────────────────────────────────────────
  function openEdit(act) {
    setEditAct(act);
    setEditForm({
      subject: act.subject, description: act.description || '',
      date: act.scheduled_date, time: act.scheduled_time?.slice(0,5) || '',
      category: act.activity_category || '',
      leadId: act.responsibility_lead_id || '',
    });
  }
  async function handleEdit() {
    setEditSaving(true);
    try {
      await morningActivityAPI.updateActivity(editAct.id, {
        subject: editForm.subject, description: editForm.description,
        date: editForm.date, time: editForm.time,
        activityCategory: editForm.category || null,
        responsibilityLeadId: editForm.leadId || null,
      });
      toast.success('Activity updated');
      setEditAct(null);
      loadActivities(); loadStats();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to update activity'); }
    finally { setEditSaving(false); }
  }

  // ── Delete Activity ───────────────────────────────────────────
  async function handleDelete() {
    try {
      await morningActivityAPI.deleteActivity(deleteId);
      toast.success('Activity deleted');
      setDeleteId(null);
      loadActivities(); loadStats();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to delete'); }
  }

  async function handleSaveNotif() {
    setNotifSaving(true);
    try {
      await morningActivityAPI.saveNotificationConfig({ configs: notifConfigs });
      toast.success('Notification configuration saved');
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
    finally { setNotifSaving(false); }
  }

  async function handleExport() {
    try {
      const range = getDateRange();
      const r = await morningActivityAPI.exportReport(range);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'morning_activities.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  }

  function updateNotif(type, field, value) {
    setNotifConfigs(cs => cs.map(c => c.notification_type === type ? { ...c, [field]: value } : c));
  }

  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <div className="p-6 space-y-5 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-800">Morning Activity</h1>
          <p className="text-slate-500 text-sm mt-0.5">Schedule, track, and document daily morning activities</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                  tab === key ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          {tab === 'activities' && (
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm">
              <Download size={15} /> Export
            </button>
          )}
        </div>
      </div>

      {/* ── DASHBOARD TAB ──────────────────────────────────────── */}
      {tab === 'dashboard' && (
        <div className="space-y-5">
          {/* 9 Status KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-9 gap-3">
            <StatCard title="Scheduled"        value={stats?.cards.scheduled       ?? 0} icon={Calendar}      color="blue"   loading={!stats} />
            <StatCard title="Due Today"         value={stats?.cards.dueToday        ?? 0} icon={CalendarCheck}  color="yellow" loading={!stats} />
            <StatCard title="Awaiting Update"   value={stats?.cards.awaitingUpdate  ?? 0} icon={Hourglass}      color="orange" loading={!stats} />
            <StatCard title="Pending Evidence"  value={stats?.cards.pendingEvidence ?? 0} icon={Upload}         color="purple" loading={!stats} />
            <StatCard title="Completed"         value={stats?.cards.completed       ?? 0} icon={CheckCircle2}   color="teal"   loading={!stats} />
            <StatCard title="Not Conducted"     value={stats?.cards.notConducted    ?? 0} icon={ClipboardX}     color="gray"   loading={!stats} />
            <StatCard title="Missed"            value={stats?.cards.missed          ?? 0} icon={XCircle}        color="red"    loading={!stats} />
            <StatCard title="Evidence Missing"  value={stats?.cards.evidenceMissing ?? 0} icon={FileWarning}    color="red"    loading={!stats} />
            <StatCard title="Rescheduled"       value={stats?.cards.rescheduled     ?? 0} icon={RefreshCw}      color="yellow" loading={!stats} />
          </div>

          {stats ? (
            <>
              {/* Analytics pct cards */}
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                <AnalyticsCard label="Completion Rate"           value={stats.analytics?.completionPct     ?? 0} color="green"  icon={TrendingUp}   />
                <AnalyticsCard label="Evidence Submission Rate"  value={stats.analytics?.evidenceSubmitPct ?? 0} color="purple" icon={Upload}        />
                <AnalyticsCard label="Missed Rate"               value={stats.analytics?.missedPct         ?? 0} color="red"    icon={XCircle}       />
                <AnalyticsCard label="Evidence Missing Rate"     value={stats.analytics?.evidenceMissingPct?? 0} color="orange" icon={FileWarning}   />
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass-card p-5 xl:col-span-2">
                  <h3 className="text-sm font-bold text-slate-700 mb-4">Weekly Trend (last 8 weeks)</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={stats.weeklyTrend} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="completed"       name="Completed"       fill="#10b981" radius={[4,4,0,0]} />
                      <Bar dataKey="missed"          name="Missed"          fill="#ef4444" radius={[4,4,0,0]} />
                      <Bar dataKey="evidence_missing"name="Evid. Missing"   fill="#7f1d1d" radius={[4,4,0,0]} />
                      <Bar dataKey="not_conducted"   name="Not Conducted"   fill="#94a3b8" radius={[4,4,0,0]} />
                      <Bar dataKey="scheduled"       name="Scheduled"       fill="#3b82f6" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="glass-card p-5">
                  <h3 className="text-sm font-bold text-slate-700 mb-4">Status Distribution (All Time)</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={stats.statusDist} cx="50%" cy="50%" outerRadius={80}
                        dataKey="value" nameKey="name"
                        label={renderPieLabel} labelLine={false}>
                        {stats.statusDist.map((entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[entry.name] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }}
                        formatter={(v, n) => [v, STATUS_CONFIG[n]?.label || n]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Monthly trend */}
              {stats.monthlyTrend?.length > 0 && (
                <div className="glass-card p-5">
                  <h3 className="text-sm font-bold text-slate-700 mb-4">Monthly Trend (last 6 months)</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={stats.monthlyTrend} barCategoryGap="35%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="completed"        name="Completed"      fill="#10b981" radius={[4,4,0,0]} />
                      <Bar dataKey="missed"           name="Missed"         fill="#ef4444" radius={[4,4,0,0]} />
                      <Bar dataKey="evidence_missing" name="Evid. Missing"  fill="#7f1d1d" radius={[4,4,0,0]} />
                      <Bar dataKey="not_conducted"    name="Not Conducted"  fill="#94a3b8" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Teacher compliance table */}
              {stats.teacherCompliance?.length > 0 && (
                <div className="glass-card p-5">
                  <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                    <Users size={15} className="text-indigo-500" />
                    Teacher-wise Compliance
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                          <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Teacher</th>
                          <th className="text-center px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Total</th>
                          <th className="text-center px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Completed</th>
                          <th className="text-center px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Missed</th>
                          <th className="text-center px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Evid. Missing</th>
                          <th className="text-center px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Compliance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {stats.teacherCompliance.map((t, i) => (
                          <tr key={i} className="hover:bg-slate-50/50">
                            <td className="px-4 py-2.5 font-medium text-slate-800">{t.teacher_name}</td>
                            <td className="px-4 py-2.5 text-center text-slate-600">{t.total}</td>
                            <td className="px-4 py-2.5 text-center text-emerald-600 font-semibold">{t.completed}</td>
                            <td className="px-4 py-2.5 text-center text-red-500">{t.missed}</td>
                            <td className="px-4 py-2.5 text-center text-red-900">{t.evidence_missing}</td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                                t.compliance_pct >= 80 ? 'bg-emerald-100 text-emerald-700'
                                : t.compliance_pct >= 50 ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                              }`}>
                                {t.compliance_pct}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Missing days */}
              {stats.missingDays?.length > 0 && (
                <div className="glass-card p-5">
                  <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                    <AlertTriangle size={15} className="text-amber-500" />
                    No Activity Scheduled — {stats.missingDays.length} day{stats.missingDays.length !== 1 ? 's' : ''} this month
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {stats.missingDays.map(d => (
                      <button key={d} onClick={() => {
                        setAddForm(f => ({ ...f, dates: [...new Set([...f.dates, d])] }));
                        setTab('add');
                      }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-xl text-xs font-medium border border-amber-200 hover:bg-amber-100">
                        <Plus size={11} /> {fmtDate(d)} ({fmtDow(d)})
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Click a date to pre-fill it in Add Activity</p>
                </div>
              )}

              {/* Pending Evidence list */}
              {stats.pendingEvidenceList?.length > 0 && (
                <div className="glass-card p-5">
                  <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                    <Clock size={15} className="text-purple-500" />
                    Pending Evidence
                    <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                      {stats.pendingEvidenceList.length}
                    </span>
                  </h3>
                  <div className="divide-y divide-slate-100">
                    {stats.pendingEvidenceList.map(act => (
                      <div key={act.id} className="flex items-center justify-between py-2.5">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{act.subject}</p>
                          <p className="text-xs text-slate-400">{fmtDate(act.scheduled_date)} · {fmtTime(act.scheduled_time)} · {act.lead_name || '—'}</p>
                        </div>
                        <button onClick={() => setEvidenceAct(act)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-xl text-xs font-semibold hover:bg-purple-700">
                          <Upload size={11} /> Submit
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-48">
              <RefreshCw size={20} className="animate-spin text-primary-500" />
            </div>
          )}
        </div>
      )}

      {/* ── ACTIVITIES TAB ─────────────────────────────────────── */}
      {tab === 'activities' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="glass-card p-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <Filter size={15} className="text-slate-400" />
              {[
                { key:'today',  label:'Today' },
                { key:'week',   label:'This Week' },
                { key:'month',  label:'This Month' },
                { key:'custom', label:'Custom' },
              ].map(({ key, label }) => (
                <button key={key} onClick={() => setDatePreset(key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                    datePreset === key ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}>
                  {label}
                </button>
              ))}
              {datePreset === 'custom' && (
                <div className="flex gap-2 items-center">
                  <input type="date" className={`${inp} !w-36`} value={customStart} onChange={e => setCustomStart(e.target.value)} />
                  <span className="text-xs text-slate-400">to</span>
                  <input type="date" className={`${inp} !w-36`} value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                </div>
              )}
              <button onClick={loadActivities}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-200">
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <select style={{width:'auto', minWidth:'160px'}} className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-primary-500"
                value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                {FILTER_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              {seeAll && (
                <select style={{width:'auto', minWidth:'140px'}} className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-primary-500"
                  value={filterLead} onChange={e => setFilterLead(e.target.value)}>
                  <option value="">All Leads</option>
                  {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
              <select style={{width:'auto', minWidth:'150px'}} className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-primary-500"
                value={filterSubject} onChange={e => setFilterSubject(e.target.value)}>
                <option value="">All Activities</option>
                {distinctSubjects.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {(filterStatus || filterLead || filterSubject) && (
                <button onClick={() => { setFilterStatus(''); setFilterLead(''); setFilterSubject(''); }}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 px-2">
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="glass-card overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw size={20} className="animate-spin text-primary-500" />
              </div>
            ) : activities.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Sunrise size={32} className="mb-2 opacity-40" />
                <p className="text-sm">No activities found for the selected filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {[
                        { key:'activity', label:'Activity' },
                        { key:'date',     label:'Date / Time' },
                        { key:'lead',     label:'Lead' },
                        { key:'status',   label:'Status' },
                      ].map(({ key, label }) => (
                        <th key={key} onClick={() => toggleSort(key)}
                          className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-slate-700 group">
                          <span className="inline-flex items-center gap-1.5">
                            {label}
                            {sortField === key
                              ? sortDir === 'asc' ? <ArrowUp size={12} className="text-primary-500" /> : <ArrowDown size={12} className="text-primary-500" />
                              : <ArrowUpDown size={12} className="text-slate-300 group-hover:text-slate-400" />}
                          </span>
                        </th>
                      ))}
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Evidence</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedActivities.map(act => {
                      const displayStatus = computeDisplayStatus(act);
                      return (
                        <tr key={act.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <p className="font-semibold text-slate-800">{act.subject}</p>
                            {act.activity_category && (
                              <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-purple-50 text-purple-600 text-[10px] font-semibold rounded-md border border-purple-100">
                                {act.activity_category}
                              </span>
                            )}
                            {act.description && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{act.description}</p>}
                            {act.original_date && <p className="text-[10px] text-amber-500 mt-0.5">Originally {fmtDate(act.original_date)}</p>}
                            {act.not_conducted_reason && (
                              <p className="text-[10px] text-slate-500 mt-0.5">Reason: {act.not_conducted_reason}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="font-medium text-slate-700">{fmtDate(act.scheduled_date)}</p>
                            <p className="text-xs text-slate-400">{fmtTime(act.scheduled_time)}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-slate-700">{act.lead_name || <span className="text-slate-300">—</span>}</p>
                            {act.lead_code && <p className="text-xs text-slate-400">{act.lead_code}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={displayStatus} />
                          </td>
                          <td className="px-4 py-3">
                            {act.evidence_id ? (
                              <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700">
                                <CheckCircle2 size={13} className="text-emerald-500" />
                                {act.photo_count > 0 ? `${act.photo_count} photo${act.photo_count !== 1 ? 's' : ''}` : 'Note only'}
                              </span>
                            ) : act.status === 'pending_evidence' ? (
                              canSubmitEvidence(act) ? (
                                <button onClick={() => setEvidenceAct(act)}
                                  className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-100 border border-purple-200">
                                  <Upload size={11} /> Submit
                                </button>
                              ) : (
                                <span className="text-xs text-red-500">Deadline passed</span>
                              )
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end items-center gap-1">
                              {/* Teacher action buttons — only for awaiting_update */}
                              {displayStatus === 'awaiting_update' && (
                                <>
                                  <button onClick={() => handleMarkConducted(act)}
                                    title="Mark as Conducted"
                                    className="flex items-center gap-1 px-2 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-xs font-semibold transition-colors">
                                    <PlayCircle size={12} /> Conducted
                                  </button>
                                  <button onClick={() => setNotConductedAct(act)}
                                    title="Mark as Not Conducted"
                                    className="flex items-center gap-1 px-2 py-1.5 bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-semibold transition-colors">
                                    <Ban size={12} /> Not Conducted
                                  </button>
                                </>
                              )}
                              {canEdit(act, seeAll) && (
                                <button onClick={() => openEdit(act)} title="Edit activity"
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                                  <Edit2 size={14} />
                                </button>
                              )}
                              {canDelete(act, seeAll) && (
                                <button onClick={() => setDeleteId(act.id)} title="Delete activity"
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-4 py-2 border-t border-slate-100 text-xs text-slate-400">
                  {sortedActivities.length} activit{sortedActivities.length !== 1 ? 'ies' : 'y'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ADD ACTIVITY TAB ───────────────────────────────────── */}
      {tab === 'add' && (
        <div className="flex flex-col xl:flex-row gap-6 items-start">
          <div className="w-full xl:w-80 shrink-0">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Select Dates <span className="text-red-400">*</span></p>
            <DatePicker selected={addForm.dates} onChange={dates => { setConflictInfo(null); setAddForm(f => ({ ...f, dates })); }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Activity Details <span className="text-red-400">*</span></p>
            <div className="glass-card p-5 space-y-4">
              {conflictInfo && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl p-3">
                  <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-800">Scheduling Conflict</p>
                    <p className="text-xs text-amber-700 mt-0.5">{conflictInfo.error}</p>
                  </div>
                  <button onClick={() => setConflictInfo(null)} className="text-amber-400 hover:text-amber-600 flex-shrink-0"><X size={14} /></button>
                </div>
              )}
              <FS label={<>Activity Name <span className="text-red-400">*</span></>}>
                <input className={inp} value={addForm.subject}
                  onChange={e => { setConflictInfo(null); setAddForm(f => ({ ...f, subject: e.target.value })); }}
                  placeholder="e.g. Morning Assembly, Sports Practice, Prayer" />
              </FS>
              <div className="flex gap-4 items-start">
                <div className="flex-1 min-w-0">
                  <FS label={<>Activity Category <span className="text-red-400">*</span></>}>
                    <select className={inp} value={addForm.category} onChange={e => setAddForm(f => ({ ...f, category: e.target.value }))}>
                      <option value="">— Select —</option>
                      {subjects.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </FS>
                </div>
                <div className="flex-1 min-w-0">
                  <FS label={<>Responsibility Lead <span className="text-red-400">*</span></>}>
                    <select className={inp} value={addForm.leadId} onChange={e => setAddForm(f => ({ ...f, leadId: e.target.value }))}>
                      <option value="">— Select —</option>
                      {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </FS>
                </div>
                <div className="shrink-0">
                  <FS label={<>Time <span className="text-red-400">*</span></>}>
                    <TimeDropdown value={addForm.time} onChange={t => { setConflictInfo(null); setAddForm(f => ({ ...f, time: t })); }} />
                  </FS>
                </div>
              </div>
              <FS label="Description">
                <textarea className={`${inp} resize-none`} rows={3} value={addForm.description}
                  onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional — agenda, objectives, notes…" />
              </FS>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setAddForm({ dates:[], time:'', subject:'', description:'', category:'', leadId:'' })}
                  className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">Clear</button>
                <button onClick={handleAdd} disabled={addSaving}
                  className="flex-1 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {addSaving
                    ? <><RefreshCw size={14} className="animate-spin" /> Creating…</>
                    : <><Plus size={14} /> Create {addForm.dates.length > 1 ? `${addForm.dates.length} Activities` : 'Activity'}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SETUP TAB ──────────────────────────────────────────── */}
      {tab === 'setup' && (
        <div className="space-y-5">
          <div className="glass-card p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-bold text-slate-700">Telegram Notifications</h3>
              <button onClick={handleSaveNotif} disabled={notifSaving}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">
                {notifSaving ? <><RefreshCw size={13} className="animate-spin" /> Saving…</> : <><Send size={13} /> Save All</>}
              </button>
            </div>
            <p className="text-xs text-slate-400 mb-5">Configure Telegram notifications for activity reminders.</p>
            <div className="bg-slate-50 rounded-xl p-3 mb-5">
              <p className="text-xs font-semibold text-slate-600 mb-2">Available Placeholders</p>
              <div className="flex flex-wrap gap-2">
                {PLACEHOLDERS.map(p => (
                  <code key={p} className="bg-white border border-slate-200 rounded-lg px-2 py-0.5 text-xs text-primary-700 font-mono">{p}</code>
                ))}
              </div>
            </div>
            <div className="space-y-5">
              {NOTIF_TYPES.map(({ key, label, icon: Icon, desc }) => {
                const cfg = notifConfigs.find(c => c.notification_type === key);
                if (!cfg) return null;
                return (
                  <div key={key} className="border border-slate-200 rounded-2xl p-5 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${cfg.enabled ? 'bg-primary-100' : 'bg-slate-100'}`}>
                          <Icon size={16} className={cfg.enabled ? 'text-primary-600' : 'text-slate-400'} />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800">{label}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
                        </div>
                      </div>
                      <button onClick={() => updateNotif(key, 'enabled', !cfg.enabled)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold ${
                          cfg.enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}>
                        {cfg.enabled ? <><Bell size={12} /> Enabled</> : <><BellOff size={12} /> Disabled</>}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FS label="Telegram Chat ID">
                        <input className={inp} value={cfg.chat_id || ''} placeholder="-1001234567890 or user chat id"
                          onChange={e => updateNotif(key, 'chat_id', e.target.value)} />
                      </FS>
                    </div>
                    <FS label="Message Template">
                      <textarea className={`${inp} resize-none font-mono text-xs`} rows={6}
                        value={cfg.template || ''} onChange={e => updateNotif(key, 'template', e.target.value)}
                        placeholder="Use {Activity_Name}, {Activity_Date}, etc." />
                    </FS>
                  </div>
                );
              })}
              {notifConfigs.length === 0 && (
                <div className="flex flex-col items-center justify-center h-32 text-slate-400">
                  <Bell size={28} className="mb-2 opacity-40" />
                  <p className="text-sm">Notification configuration not loaded yet.</p>
                  <button onClick={loadNotifConfig} className="mt-2 text-xs text-primary-500 hover:underline">Try again</button>
                </div>
              )}
            </div>
          </div>
          <NotificationThresholdCard
            module="morningActivity"
            yearId={3}
            title="Activity Completion Thresholds"
            thresholds={[
              {
                key: 'min_completion_pct',
                label: 'Minimum daily completion rate (%)',
                description: 'Send a notification if the percentage of conducted activities for the day falls below this value.',
                defaultValue: 80,
                min: 0,
                max: 100,
                suffix: '% completion / day',
              },
            ]}
          />
          <EmailNotificationSetup    module="morningActivity" moduleLabel="Morning Activity" yearId={3} />
          <TelegramNotificationSetup module="morningActivity" moduleLabel="Morning Activity" yearId={3} />
        </div>
      )}

      {/* ── EVIDENCE DRAWER ─────────────────────────────────────── */}
      {evidenceAct && (
        <EvidenceDrawer activity={evidenceAct} onClose={() => setEvidenceAct(null)}
          onSaved={() => { loadActivities(); loadStats(); }} />
      )}

      {/* ── NOT CONDUCTED MODAL ─────────────────────────────────── */}
      {notConductedAct && (
        <NotConductedModal activity={notConductedAct} onClose={() => setNotConductedAct(null)}
          onSaved={() => { loadActivities(); loadStats(); }} />
      )}

      {/* ── EDIT MODAL ──────────────────────────────────────────── */}
      {editAct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800">Edit Activity</h3>
              <button onClick={() => setEditAct(null)} className="p-2 rounded-xl hover:bg-slate-100"><X size={17} /></button>
            </div>
            <div className="p-5 space-y-4">
              <FS label="Activity Name *">
                <input className={inp} value={editForm.subject} onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))} />
              </FS>
              <FS label="Activity Category">
                <select className={inp} value={editForm.category || ''} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}>
                  <option value="">— Select category —</option>
                  {subjects.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </FS>
              <div className="grid grid-cols-2 gap-4">
                <FS label="Date *">
                  <input type="date" className={inp} value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
                </FS>
                <FS label="Time *">
                  <TimeDropdown value={editForm.time} onChange={t => setEditForm(f => ({ ...f, time: t }))} />
                </FS>
              </div>
              <FS label="Description">
                <textarea className={`${inp} resize-none`} rows={2} value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
              </FS>
              <FS label="Responsibility Lead">
                <select className={inp} value={editForm.leadId} onChange={e => setEditForm(f => ({ ...f, leadId: e.target.value }))}>
                  <option value="">None</option>
                  {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </FS>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
                Status is system-managed. Use "Mark as Conducted" / "Mark as Not Conducted" buttons in the activities table to update status.
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-slate-100">
              <button onClick={() => setEditAct(null)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              {canDelete(editAct, seeAll) && (
                <button onClick={() => { setDeleteId(editAct.id); setEditAct(null); }}
                  className="px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm font-semibold hover:bg-red-100">Delete</button>
              )}
              <button onClick={handleEdit} disabled={editSaving}
                className="flex-1 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {editSaving ? <><RefreshCw size={14} className="animate-spin" /> Saving…</> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM ──────────────────────────────────────── */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h3 className="font-bold text-slate-800 mb-1">Delete Activity?</h3>
            <p className="text-sm text-slate-500 mb-6">This will also delete any submitted evidence and photos. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
